// 「この端末の記録をアカウントにまとめます」の確認モーダル（docs/AUTH.md の実装順序(5)）。
//
// 🔴 **統合は自動で走らせない**。合算する項目があるので取り消せないし、何より
// 「ログインしたら記録が混ざった」を後から説明できない。**何が起きるかを数字で見せてから**実行する。
//
// ⚠️ **［やめる］はログインごと中止**（呼び出し側の約束）。統合せずにログインだけ通すと、
//    次の同期でサーバーの進捗がローカルを置き換え、この端末の記録が**黙って消える**。
import { t } from '@/platform/i18n';
import {
  type MergeContributions,
  type MergePreview,
  commitMerge,
  newMergeRequestId,
  previewMerge,
} from '@/platform/net/merge';
import { el, openOverlay } from './modalChrome';

/**
 * 下見 → 確認 → 統合まで面倒を見る。
 * - `true`  … 統合できた（呼び出し側はログインを続行してよい）
 * - `false` … 利用者がやめた／下見や統合に失敗した（**ログインごと中止する**）
 */
export function confirmMerge(games: MergeContributions): Promise<boolean> {
  return new Promise((resolve) => {
    const { overlay, panel, close } = openOverlay();
    // ⚠️ **再試行でも同じ値を使う**。変えると合算項目が二重に足される。
    const requestId = newMergeRequestId();

    const title = el('div', 'hs-modal-title', t('portal.merge.title'));
    const body = el('div', 'hs-modal-body', t('portal.merge.checking'));
    const err = el('div', 'hs-modal-err', '');
    const row = el('div', 'hs-modal-row');
    const cancelBtn = el('button', 'hs-btn hs-btn-ghost', t('portal.merge.cancel'));
    const okBtn = el('button', 'hs-btn hs-btn-primary', t('portal.merge.submit'));
    // 下見が返るまでは押させない（何が起きるか分からないまま押せてしまうため）。
    (okBtn as HTMLButtonElement).disabled = true;
    row.append(cancelBtn, okBtn);
    panel.append(title, body, err, row);

    const done = (v: boolean) => {
      close();
      resolve(v);
    };

    const busy = (on: boolean) => {
      (okBtn as HTMLButtonElement).disabled = on;
      (cancelBtn as HTMLButtonElement).disabled = on;
    };

    /** 下見の数字を本文へ流し込む。 */
    const showPreview = (p: MergePreview) => {
      body.replaceChildren(
        el('div', '', t('portal.merge.body')),
        el(
          'div',
          'hs-merge-stars',
          p.merged > p.account
            ? t('portal.merge.starsGain', { before: p.account, after: p.merged })
            : t('portal.merge.starsSame', { stars: p.merged }),
        ),
        el('div', 'hs-field-note', t('portal.merge.safe')),
      );
    };

    const run = async (): Promise<void> => {
      busy(true);
      okBtn.textContent = t('portal.merge.working');
      err.textContent = '';
      const result = await commitMerge(requestId, games);
      if (!result) {
        err.textContent = t('portal.merge.error');
        okBtn.textContent = t('portal.merge.submit');
        busy(false);
        return;
      }
      done(true);
    };

    okBtn.addEventListener('click', () => void run());
    cancelBtn.addEventListener('click', () => done(false));
    overlay.addEventListener('mousedown', (e) => {
      if (e.target === overlay) done(false);
    });

    // 下見。失敗したらボタンを出さずに閉じる＝**中途半端な状態でログインさせない**。
    void previewMerge(games).then((p) => {
      if (!p) {
        err.textContent = t('portal.merge.error');
        body.textContent = '';
        okBtn.remove();
        cancelBtn.textContent = t('portal.merge.close');
        return;
      }
      showPreview(p);
      (okBtn as HTMLButtonElement).disabled = false;
    });
  });
}
